/*
 * Copyright (C) 2018 - 2026 Entgra (Pvt) Ltd, Inc - All Rights Reserved.
 *
 * Unauthorised copying/redistribution of this file, via any medium is strictly prohibited.
 *
 * Licensed under the Entgra Commercial License, Version 1.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *       https://entgra.io/licenses/entgra-commercial/1.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

/** The instant KPIs the backend streams, by the id used in the stream's `kpis` parameter. */
export const INSTANT_KPIS = ['total-demand', 'power-factor', 'meter-status'] as const;
export type InstantKpiId = (typeof INSTANT_KPIS)[number];

/** One value per phase; null when no reading has reported that phase (never zero). */
export interface PhaseValues {
  a: number | null;
  b: number | null;
  c: number | null;
}

export interface IncomerDemand {
  serial: string;
  /** The sum of its phases, or the meter's own total when it reports none (single-phase meters). */
  totalKw: number | null;
  /** Active power per phase, kW. */
  phases: PhaseValues;
  /** 3 when phase B or C has reported, otherwise 1. */
  phaseCount: number;
  readingTimestamp: number | null;
  /** No recent reading: shown with its last value. */
  stale: boolean;
}

/** The live demand of each incomer meter (`instant-kpi.total-demand`); there is no figure across meters. */
export interface TotalDemandKpi {
  incomers: IncomerDemand[];
  updatedAt: number | null;
}

export interface IncomerPowerFactor {
  serial: string;
  /** Load-weighted over its phases. */
  average: number | null;
  phases: PhaseValues;
  /** 3 when phase B or C has reported, otherwise 1. */
  phaseCount: number;
  readingTimestamp: number | null;
  /** No recent reading: shown with its last value. */
  stale: boolean;
}

/** The live power factor of each incomer meter (`instant-kpi.power-factor`); there is no figure across meters. */
export interface PowerFactorKpi {
  incomers: IncomerPowerFactor[];
  updatedAt: number | null;
}

export interface OfflineMeter {
  serial: string;
  name: string;
  /** When its AMI status last changed, epoch ms. */
  offlineSince: number | null;
}

/** Online / offline power meters (`instant-kpi.meter-status`); counts are null until AMI has been read. */
export interface MeterStatusKpi {
  onlineCount: number | null;
  offlineCount: number | null;
  /** Every meter, including ones not in service. */
  totalCount: number | null;
  /** Most recently gone offline first. */
  recentOffline: OfflineMeter[];
  updatedAt: number | null;
}

export type InstantKpiValue<K extends InstantKpiId> = K extends 'total-demand'
  ? TotalDemandKpi
  : K extends 'power-factor'
    ? PowerFactorKpi
    : MeterStatusKpi;

function num(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function phases(value: unknown): PhaseValues {
  const raw = value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  return { a: num(raw.a), b: num(raw.b), c: num(raw.c) };
}

/**
 * An incomer's phases and phase count. Without a sent count (an older backend), it is 3 when phase B or C has a
 * value, as the backend counts.
 */
function phasesWithCount(incomer: Record<string, unknown>): { phases: PhaseValues; phaseCount: number } {
  const values = phases(incomer.phases);
  const sent = num(incomer.phaseCount);
  const threePhase = sent !== null ? sent === 3 : values.b !== null || values.c !== null;
  return { phases: values, phaseCount: threePhase ? 3 : 1 };
}

function incomers(raw: Record<string, unknown>): Record<string, unknown>[] | null {
  if (!Array.isArray(raw.incomers)) return null;
  return raw.incomers
    .filter((incomer): incomer is Record<string, unknown> => !!incomer && typeof incomer === 'object')
    .filter((incomer) => typeof incomer.serial === 'string');
}

function asObject(data: unknown): Record<string, unknown> | null {
  return data && typeof data === 'object' ? (data as Record<string, unknown>) : null;
}

/** Parses a total demand event; null for anything that is not one. */
export function parseTotalDemand(data: unknown): TotalDemandKpi | null {
  const raw = asObject(data);
  const list = raw && incomers(raw);
  if (!raw || !list) return null;
  return {
    incomers: list.map((incomer) => ({
      serial: incomer.serial as string,
      totalKw: num(incomer.totalKw),
      ...phasesWithCount(incomer),
      readingTimestamp: num(incomer.readingTimestamp),
      stale: incomer.stale === true,
    })),
    updatedAt: num(raw.updatedAt),
  };
}

/** Parses a power factor event; null for anything that is not one. */
export function parsePowerFactor(data: unknown): PowerFactorKpi | null {
  const raw = asObject(data);
  const list = raw && incomers(raw);
  if (!raw || !list) return null;
  return {
    incomers: list.map((incomer) => ({
      serial: incomer.serial as string,
      average: num(incomer.average),
      ...phasesWithCount(incomer),
      readingTimestamp: num(incomer.readingTimestamp),
      stale: incomer.stale === true,
    })),
    updatedAt: num(raw.updatedAt),
  };
}

/** Parses a meter status event; null for anything that is not one. */
export function parseMeterStatus(data: unknown): MeterStatusKpi | null {
  const raw = asObject(data);
  if (!raw || !Array.isArray(raw.recentOffline)) return null;
  return {
    onlineCount: num(raw.onlineCount),
    offlineCount: num(raw.offlineCount),
    totalCount: num(raw.totalCount),
    recentOffline: raw.recentOffline
      .filter((meter): meter is Record<string, unknown> => !!meter && typeof meter === 'object')
      .filter((meter) => typeof meter.serial === 'string')
      .map((meter) => ({
        serial: meter.serial as string,
        name: typeof meter.name === 'string' && meter.name !== '' ? meter.name : (meter.serial as string),
        offlineSince: num(meter.offlineSince),
      })),
    updatedAt: num(raw.updatedAt),
  };
}

export function parseInstantKpi<K extends InstantKpiId>(kpi: K, data: unknown): InstantKpiValue<K> | null {
  const parsed = kpi === 'total-demand' ? parseTotalDemand(data) : kpi === 'power-factor' ? parsePowerFactor(data) : parseMeterStatus(data);
  return parsed as InstantKpiValue<K> | null;
}
